import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DataOutIntegration as UIAPIDataOutIntegration,
  IntegrationsVerifyDataOutResponse,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { clearDataOutIntegrationCache, setDataOutIntegrationCache } from './cache';
import { parseDataOutIntegration, type DataOutIntegration, type DataOutIntegrationWriteInput } from './entity';
import { withDerivedEnabled } from './utils';

/**
 * Data-out connector writes: connect (POST), update (PUT), disconnect (DELETE),
 * and the one-off "test connection" send.
 *
 * The backend owns validation, so saves are not optimistic — each hook writes
 * the canonical response into the single detail slot the directory and
 * connections overview also read.
 *
 * Connect and update are separate endpoints on the same URL, and the split is
 * behavioral, not cosmetic: POST creates and 409s on an existing record, while
 * PUT requires the full field set and merges settings over the stored ones.
 */

function requireDataOut(res: { data_out?: UIAPIDataOutIntegration }): UIAPIDataOutIntegration {
  if (res.data_out == null) {
    throw new Error('data-out integration response did not include a data_out record');
  }
  return res.data_out;
}

/* ------------------------------------------------------------------------- */
/* createDataOutIntegration                                                  */
/* ------------------------------------------------------------------------- */

export interface CreateDataOutIntegrationInput extends DataOutIntegrationWriteInput {
  workspaceId: number;
  identifier: string;
}

/** @public */
export async function createDataOutIntegration({
  workspaceId,
  identifier,
  ...write
}: CreateDataOutIntegrationInput): Promise<DataOutIntegration> {
  const res = await hydraUiApi.integrations.createDataOut({
    params: { path: { environment_id: String(workspaceId), identifier } },
    body: { data_out: withDerivedEnabled(write, true) },
  });
  return parseDataOutIntegration(requireDataOut(res));
}

type UseCreateDataOutIntegrationVariables = Omit<CreateDataOutIntegrationInput, 'workspaceId'>;

interface UseCreateDataOutIntegrationOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<DataOutIntegration, UseCreateDataOutIntegrationVariables>;
}

/** @public */
export const useCreateDataOutIntegration = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseCreateDataOutIntegrationOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseCreateDataOutIntegrationVariables) =>
      createDataOutIntegration({ workspaceId, ...variables }),
    onSuccess: (created, ...callbackArgs) => {
      setDataOutIntegrationCache(qc, workspaceId, created);
      onSuccess?.(created, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* updateDataOutIntegration                                                  */
/* ------------------------------------------------------------------------- */

export interface UpdateDataOutIntegrationInput extends DataOutIntegrationWriteInput {
  workspaceId: number;
  identifier: string;
}

/** @public */
export async function updateDataOutIntegration({
  workspaceId,
  identifier,
  ...write
}: UpdateDataOutIntegrationInput): Promise<DataOutIntegration> {
  const res = await hydraUiApi.integrations.updateDataOut({
    params: { path: { environment_id: String(workspaceId), identifier } },
    body: { data_out: withDerivedEnabled(write, false) },
  });
  return parseDataOutIntegration(requireDataOut(res));
}

type UseUpdateDataOutIntegrationVariables = Omit<UpdateDataOutIntegrationInput, 'workspaceId'>;

interface UseUpdateDataOutIntegrationOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<DataOutIntegration, UseUpdateDataOutIntegrationVariables>;
}

/** @public */
export const useUpdateDataOutIntegration = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseUpdateDataOutIntegrationOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseUpdateDataOutIntegrationVariables) =>
      updateDataOutIntegration({ workspaceId, ...variables }),
    onSuccess: (updated, ...callbackArgs) => {
      setDataOutIntegrationCache(qc, workspaceId, updated);
      onSuccess?.(updated, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* deleteDataOutIntegration                                                  */
/* ------------------------------------------------------------------------- */

export interface DeleteDataOutIntegrationInput {
  workspaceId: number;
  identifier: string;
}

/** @public */
export async function deleteDataOutIntegration(input: DeleteDataOutIntegrationInput): Promise<void> {
  await hydraUiApi.integrations.deleteDataOut({
    params: { path: { environment_id: String(input.workspaceId), identifier: input.identifier } },
  });
}

type UseDeleteDataOutIntegrationVariables = Omit<DeleteDataOutIntegrationInput, 'workspaceId'>;

interface UseDeleteDataOutIntegrationOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteDataOutIntegrationVariables>;
}

/** @public */
export const useDeleteDataOutIntegration = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseDeleteDataOutIntegrationOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteDataOutIntegrationVariables) =>
      deleteDataOutIntegration({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      clearDataOutIntegrationCache(qc, workspaceId, variables.identifier);
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* verifyDataOutIntegration                                                  */
/* ------------------------------------------------------------------------- */

export interface VerifyDataOutIntegrationInput {
  workspaceId: number;
  identifier: string;
  settings: DataOutIntegrationWriteInput['settings'];
}

/**
 * @public Send a test event through the connector's settings.
 *
 * Writes nothing to the cache — the result is transient feedback next to the
 * "Test connection" button. Only `settings` crosses the wire: the test event,
 * its channel and its payload are all fixed server-side, so the rest of the
 * record has no bearing on whether the credentials work.
 *
 * A failed test is a **400**, not a field on the success body, so callers branch
 * on the mutation's error state rather than inspecting the result.
 */
export async function verifyDataOutIntegration({
  workspaceId,
  identifier,
  settings,
}: VerifyDataOutIntegrationInput): Promise<IntegrationsVerifyDataOutResponse> {
  return await hydraUiApi.integrations.dataOutVerify({
    params: { path: { environment_id: String(workspaceId), identifier } },
    body: { data_out: { settings } },
  });
}

type UseVerifyDataOutIntegrationVariables = Omit<VerifyDataOutIntegrationInput, 'workspaceId'>;

interface UseVerifyDataOutIntegrationOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<IntegrationsVerifyDataOutResponse, UseVerifyDataOutIntegrationVariables>;
}

/** @public */
export const useVerifyDataOutIntegration = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseVerifyDataOutIntegrationOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseVerifyDataOutIntegrationVariables) =>
      verifyDataOutIntegration({ workspaceId, ...variables }),
    onSuccess,
  });
};
